import { Classes, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { QuantityInput } from 'browser/components/atomic-elements/atoms/input/quantity-input'
import {
  HtmlTableRow,
  IHtmlTableRowProps,
} from 'browser/components/atomic-elements/atoms/table/html-table/row'
import { InputField } from 'browser/components/atomic-elements/molecules/fields/input-field/input-field'
import { SelectField } from 'browser/components/atomic-elements/molecules/fields/select-field'
import { ConfirmationModal } from 'browser/components/atomic-elements/organisms/confirmation-modal'

const PICK_UP = 'Pick Up'
const DROP_OFF = 'Drop Off'

// tslint:disable-next-line:max-line-length
import 'browser/components/atomic-elements/domains/trucking/order-cargo-delivery-task-row/_order-cargo-delivery-task-row.scss'
import { components, SingleValueProps } from 'react-select'

const WeightOptions = [
  { label: 'lbs', value: 'Pounds' },
  { label: 'kgs', value: 'Kilograms' },
]

interface IQuantity {
  unit: string
  value: string
}

interface ICargo {
  description?: string
  uniqueId?: string
  quantity?: IQuantity
  value?: IQuantity
  weight?: IQuantity
}

interface ICargoTask {
  cargo?: any
  description?: any
  type?: string
  quantity?: IQuantity
  value?: IQuantity
  weight?: IQuantity
}

interface IItemTypeOption {
  cargo: ICargo
  label: string
  type: string
  value: string | undefined
}

interface IPropertyChangeOptions {
  shouldUpdateCargo: boolean
  shouldUpdateItem: boolean
}

/**
 * @prop cargoQuantityUnitSchemaPath - path in the parent schema to the definition of cargoQuantity units
 * @prop cargosPath - path in the entity to cargos
 * @prop stopsPath - path in the entity to stops
 * @prop entity - either a salesOrder or trip entity
 * @prop frames
 * @prop errorsMap - input errors mapped to entity properties
 * @prop value - a CargoTask, comprised of cargo and related information about that cargo
 *
 * @uiComponent
 */
export interface ICargoDeliveryTaskRowProps extends IHtmlTableRowProps {
  cargoQuantityUnitSchemaPath: string
  cargosPath: string
  stopsPath: string
  entity: any
  frames: any
  errorsMap: any
  value: ICargoTask
}

interface ICargoDeliveryTaskRowState {
  cargo: ICargo
  defaultQuantityUnit: string
  pickupCargo: ICargo
  pickupOptions: IItemTypeOption[]
  stopIndex: string | number
  quantityOptions: any[]
}

/**
 * @component CargoDeliverTaskRow - table row to display cargoTasks at a stop
 *
 * @props ICargoDeliveryTaskRowProps
 */
export class CargoDeliveryTaskRow extends React.Component<
  ICargoDeliveryTaskRowProps,
  ICargoDeliveryTaskRowState
> {
  private htmlTableRow?: HtmlTableRow | null

  constructor(props: ICargoDeliveryTaskRowProps) {
    super(props)

    // get cargoSchema to find default values and enums
    const { entity, frames, cargosPath, cargoQuantityUnitSchemaPath } = props
    const value = this.getValue()
    const quantityTypeSchema = entity.resolveSubschemaByPath(cargoQuantityUnitSchemaPath).schema
    const quantityTypeEnums = _.get(quantityTypeSchema, 'suggestions', [])
    const defaultQuantityUnit = quantityTypeSchema.default
    const quantityOptions = _.map(quantityTypeEnums, (option) => {
      return { label: option, value: option }
    })

    // get cargo
    const cargos: ICargo[] = entity.get(cargosPath, [])
    const cargoItemId = _.get(value, 'cargo.itemId')
    const cargo: ICargo = _.find(cargos, { uniqueId: cargoItemId }) || {}

    // get stopIndex - dispatchOrder, stops, <stopIndex>, cargos, <cargoIndex>
    const valuePath = frames.getContext('valuePath')
    const stopIndex = valuePath[2]

    // if value is a pickup, its cargo is already pickup cargo. otherwise create a new one
    const pickupCargo: ICargo =
      value.type === PICK_UP
        ? cargo
        : {
            uniqueId: uuidv4(),
          }
    const pickupOptions: IItemTypeOption[] = [
      {
        cargo: pickupCargo,
        label: PICK_UP,
        type: PICK_UP,
        value: pickupCargo.uniqueId,
      },
    ]

    this.state = {
      cargo,
      defaultQuantityUnit,
      pickupCargo,
      pickupOptions,
      quantityOptions,
      stopIndex,
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: ICargoDeliveryTaskRowProps) {
    const { entity, value, cargosPath } = nextProps
    const cargoId = _.get(this.state.cargo, 'uniqueId')
    const cargoItemId = _.get(value, 'cargo.itemId')
    if (cargoId !== cargoItemId) {
      const cargos: ICargo[] = entity.get(cargosPath, [])
      const cargo: ICargo = _.find(cargos, { uniqueId: cargoItemId }) || {}
      this.setState({ cargo })
    }
  }

  public focus() {
    this.htmlTableRow!.focus()
  }

  public render() {
    const { cargo } = this.state
    const value = this.getValue()
    const hasItemType = !!value.type
    const isPickup = value.type === PICK_UP
    return (
      <HtmlTableRow {...this.props} ref={this.handleRef} renderControls={this.renderRemoveButton}>
        {this.renderTypeSelect(value)}
        {this.renderCargoDescription(cargo, hasItemType, isPickup)}
        {this.renderQuantity(value, hasItemType, isPickup)}
        {this.renderWeight(cargo, value, hasItemType, isPickup)}
      </HtmlTableRow>
    )
  }

  //////////////////////////////////////////////////////////////////////////////
  // Helper functions
  //////////////////////////////////////////////////////////////////////////////

  private getCargos(): ICargo[] {
    const { entity, cargosPath } = this.props
    return entity.get(cargosPath, [])
  }

  private getValue(): ICargoTask {
    return this.props.value || {}
  }

  private showCargoDeleteConfirmation() {
    return new Promise((resolve, reject) => {
      ConfirmationModal.open({
        confirmationText: 'Do you want to delete this cargo?',
        confirmationTitle: 'Review Changes',
        modalDialogClassName: 'c-modal-dialog--sm',
        onPrimaryClicked: resolve,
        primaryButtonText: 'Confirm',
      })
    })
  }

  private getItemTypeOptions() {
    const { pickupCargo, pickupOptions, stopIndex } = this.state
    const cargos = this.getCargos()
    // Cargos in first stop can only have Pick Up
    if (stopIndex === 0) {
      return pickupOptions
    }
    // Cargos in other stop can have drop offs. However, the drop off cannot
    // include the pickup cargo in this row
    const dropOffCargos = _.filter(cargos, (cargo) => {
      return cargo.uniqueId !== pickupCargo.uniqueId
    })
    const dropOffOptions: IItemTypeOption[] = []
    _.forEach(dropOffCargos, (cargo, cargoIndex) => {
      if (_.isEmpty(cargo.description)) {
        return
      }
      const value = `${DROP_OFF} - Cargo ${cargo.description}`
      dropOffOptions.push({
        cargo,
        label: value,
        type: DROP_OFF,
        value: cargo.uniqueId,
      })
    })
    return pickupOptions.concat(dropOffOptions)
  }

  //////////////////////////////////////////////////////////////////////////////
  // Cargo and Stop Cargo Sync
  //////////////////////////////////////////////////////////////////////////////

  private associateCargoWithStopCargo(itemType: string, cargo: ICargo, value: ICargoTask) {
    const { entity, onChange, cargosPath } = this.props
    const cargos = this.getCargos()
    value.cargo = { itemId: cargo.uniqueId }
    value.type = itemType
    if (itemType === PICK_UP) {
      // add cargo to dispatchOrder.cargos if it does not exist
      if (!_.find(cargos, { uniqueId: cargo.uniqueId })) {
        cargos.push(cargo)
        entity.set(cargosPath, cargos)
      }
    } else if (itemType === DROP_OFF) {
      // copy quantity, weight and value from cargo to item
      value.quantity = _.cloneDeep(cargo.quantity)
      value.weight = _.cloneDeep(cargo.weight)
      value.value = _.cloneDeep(cargo.value)
    }
    this.setState({ cargo })
    onChange!(value)
  }

  private deleteCargoAndAssociatedDropOffs() {
    const cargos = this.getCargos()
    const { cargo } = this.state
    const { entity, stopsPath } = this.props
    // delete cargo from cargos array
    _.remove(cargos, { uniqueId: cargo.uniqueId })

    // delete any drop off for this cargo
    const stops = entity.get(stopsPath, [])
    _.forEach(stops, (stop, stopIndex) => {
      _.remove(stop.cargos, (stopCargo: ICargoTask) => {
        const cargoItemId = _.get(stopCargo, 'cargo.itemId')
        return stopCargo.type === DROP_OFF && cargoItemId === cargo.uniqueId
      })
    })
  }

  //////////////////////////////////////////////////////////////////////////////
  // Handlers
  //////////////////////////////////////////////////////////////////////////////

  private handleRef = (ref: HtmlTableRow | null) => {
    this.htmlTableRow = ref
  }

  private handleItemTypeChange = (ununsed: string | undefined, option: IItemTypeOption) => {
    const { cargo } = option
    const value = this.getValue()
    const previousItemType = value.type
    const newItemType = option.type
    if (previousItemType === PICK_UP && newItemType === DROP_OFF) {
      this.showCargoDeleteConfirmation().then(() => {
        this.deleteCargoAndAssociatedDropOffs()
        this.associateCargoWithStopCargo(newItemType, cargo, value)
      })
    } else {
      this.associateCargoWithStopCargo(newItemType, cargo, value)
    }
  }

  private handlePropertyChange = (
    key: string,
    newValue: string | IQuantity | undefined,
    { shouldUpdateCargo, shouldUpdateItem }: IPropertyChangeOptions
  ) => {
    const { onChange } = this.props
    const { cargo } = this.state
    const value = this.getValue()
    if (shouldUpdateCargo) {
      _.set(cargo, key, newValue)
    }
    if (shouldUpdateItem) {
      _.set(value, key, newValue)
    }
    onChange!(value)
  }

  private handleDeleteCargo = () => {
    const { onRemove, value } = this.props
    if (value.type !== PICK_UP) {
      onRemove!()
      return
    }
    this.showCargoDeleteConfirmation().then(() => {
      this.deleteCargoAndAssociatedDropOffs()
      onRemove!()
    })
  }

  //////////////////////////////////////////////////////////////////////////////
  // Renderers
  //////////////////////////////////////////////////////////////////////////////

  private getErrorText(paths: string[]): string | undefined {
    const { errorsMap } = this.props
    for (let i = 0; i < paths.length; ++i) {
      const errorText = _.get(errorsMap, [paths[i], '_errors', 0], null)
      if (errorText) {
        return errorText
      }
    }
  }

  private renderTypeSelect(item: ICargoTask) {
    const errorText = this.getErrorText(['type'])
    const selectOptions = this.getItemTypeOptions()
    const isDisabled = _.isEmpty(item.type) && _.isEmpty(selectOptions)
    const cargoId = _.get(item, 'cargo.itemId')
    return (
      <SelectField
        className={classNames({
          'c-orderCargoDeliveryTaskRow-typeSelect': selectOptions.length > 1,
        })}
        errorText={isDisabled ? '' : errorText}
        size={this.props.size}
        isDisabled={isDisabled}
        onChange={this.handleItemTypeChange}
        options={selectOptions}
        value={cargoId}
        valueRenderer={this.renderTypeValue}
      />
    )
  }

  private renderCargoDescription(cargo: ICargo, hasItemType: boolean, isPickup: boolean) {
    const options = { shouldUpdateCargo: isPickup, shouldUpdateItem: false }
    const errorText = this.getErrorText(['description'])
    const isDisabled = !hasItemType || !isPickup
    const handlePropertyChange = (value: string) => {
      this.handlePropertyChange('description', value, options)
    }
    return (
      <InputField
        errorText={isDisabled ? '' : errorText}
        isDisabled={isDisabled}
        onChange={handlePropertyChange}
        size={this.props.size}
        value={cargo.description}
      />
    )
  }

  private renderQuantity(item: ICargoTask, hasItemType: boolean, isPickup: boolean) {
    const { errorsMap, size } = this.props
    const { defaultQuantityUnit, quantityOptions } = this.state
    const options = { shouldUpdateCargo: isPickup, shouldUpdateItem: true }
    const defaultUnit: string = defaultQuantityUnit
    const isDisabled = !hasItemType
    const isUnitDisabled = !isPickup
    const value: IQuantity | null | undefined = _.get(item, 'quantity', null)
    const handlePropertyChange = (val: IQuantity | undefined) => {
      this.handlePropertyChange('quantity', val, options)
    }
    return (
      <QuantityInput
        defaultUnit={defaultUnit}
        errorsMap={isDisabled ? null : _.get(errorsMap, 'quantity')}
        isDisabled={isDisabled}
        isUnitDisabled={isUnitDisabled}
        onChange={handlePropertyChange}
        size={size}
        unitOptions={quantityOptions}
        value={value}
      />
    )
  }

  private renderWeight(cargo: ICargo, item: ICargoTask, hasItemType: boolean, isPickup: boolean) {
    const { errorsMap, size } = this.props
    const options = { shouldUpdateCargo: isPickup, shouldUpdateItem: true }
    const defaultUnit: string = _.get(cargo, 'weight.unit', 'Pounds')
    const isDisabled = !hasItemType
    const isUnitDisabled = !isPickup
    const value: IQuantity | null | undefined = _.get(item, 'weight', null)
    const handlePropertyChange = (val: IQuantity | undefined) => {
      this.handlePropertyChange('weight', val, options)
    }
    return (
      <QuantityInput
        defaultUnit={defaultUnit}
        errorsMap={isDisabled ? null : _.get(errorsMap, 'weight')}
        isDisabled={isDisabled}
        isUnitDisabled={!isPickup}
        onChange={handlePropertyChange}
        size={size}
        unitOptions={WeightOptions}
        value={value}
      />
    )
  }

  private renderRemoveButton = () => {
    const { density, onRemove, size } = this.props
    const densityClassName = _.isEmpty(density) ? '' : `c-table-cell--${density}`
    const sizeClassName = _.isEmpty(size) ? '' : `c-table-cell--${size}`
    return (
      <td
        className={classNames('c-table-cell c-table-cell--delete', sizeClassName, densityClassName)}
      >
        <Button
          className={classNames('c-button--square c-table-cellDeleteButton', Classes.MINIMAL)}
          isDisabled={!onRemove}
          onClick={this.handleDeleteCargo}
          size={size}
        >
          <Icon icon={IconNames.CROSS} />
        </Button>
      </td>
    )
  }

  private renderTypeValue = (props: SingleValueProps<IItemTypeOption>) => {
    return <components.SingleValue {...props}>{props.data.type}</components.SingleValue>
  }
}
